import { ScriptDescriptor } from '../interfaces';
import clarity from '../scripts/clarity';
import cookiehubScript from '../scripts/cookiehub';
import gtmScript from '../scripts/gtm';
import hotjar from '../scripts/hotjar';
/**
 * Per-environment third-party script builders.
 *
 * These reproduce the HTML snippets that the retired Bitbucket Pipelines
 * configmap script used to inject into `index.html` at deploy time, but as runtime
 * {@link ScriptDescriptor}s loaded by the `ScriptLoaderService`. IDs/keys vary
 * per environment and are supplied by each `environment.*.ts`.
 */
export interface ThirdPartyScriptOptions {
    /** CookieHub consent-manager loader URL (per brand/env). */
    cookieHubSrc?: string;
    /** Google Tag Manager container id, e.g. `GTM-WK53JCD`. */
    gtmId?: string;
    /** Microsoft Clarity project id. */
    clarityId?: string;
    /** Hotjar site id (`hjid`). */
    hotjarId?: number;
    /** Jira issue-collector script URL(s). Strabag ships two (jQuery + collector). */
    jiraCollectorSrcs?: string[];
}

/**
 * Assembles the ordered script list for an environment. CookieHub is injected
 * first so it is present to gate the analytics that follow; the GTM `<noscript>`
 * fallback goes last, into `<body>`.
 */
export function buildThirdPartyScripts(options: ThirdPartyScriptOptions): ScriptDescriptor[] {
    const scripts: ScriptDescriptor[] = [];

    if (options.cookieHubSrc) scripts.push(...cookieHubScripts(options.cookieHubSrc));
    if (options.gtmId) scripts.push(gtmHeadScript(options.gtmId));
    if (options.clarityId) scripts.push(clarityScript(options.clarityId));
    if (options.hotjarId) scripts.push(hotjarScript(options.hotjarId));
    if (options.jiraCollectorSrcs?.length) scripts.push(...jiraCollectorScripts(options.jiraCollectorSrcs));
    if (options.gtmId) scripts.push(gtmBodyNoscript(options.gtmId));

    return scripts;
}

/**
 * CookieHub consent manager: the external loader plus an init call.
 *
 * The original snippet called `window.cookiehub.load()` on `DOMContentLoaded`.
 * Because scripts are now injected after Angular bootstraps (DOM already
 * parsed), the init waits for the loader to be available instead, otherwise the
 * already-fired `DOMContentLoaded` event would never trigger it.
 */
function cookieHubScripts(src: string): ScriptDescriptor[] {
    return [
        { id: 'cookiehub', src, target: 'head' },
        {
            id: 'cookiehub-init',
            target: 'head',
            content: cookiehubScript,
        },
    ];
}

function gtmHeadScript(gtmId: string): ScriptDescriptor {
    return {
        id: 'gtm-head',
        target: 'head',
        content: gtmScript(gtmId),
    };
}

function gtmBodyNoscript(gtmId: string): ScriptDescriptor {
    return {
        id: 'gtm-body',
        tag: 'noscript',
        target: 'body',
        content: `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display: none; visibility: hidden"></iframe>`,
    };
}

/**
 * Microsoft Clarity, kept as the vendor's inline snippet rather than a bare
 * `src` to `clarity.ms/tag/<id>`. That tag file's first statement calls
 * `window.clarity(...)`, so it only works when the snippet's queue stub already
 * exists; loading it on its own throws `TypeError: a[c] is not a function` and
 * no Clarity call is ever recorded. The snippet defines the stub and then
 * injects the tag itself. SAM-12528
 */
function clarityScript(clarityId: string): ScriptDescriptor {
    return {
        id: 'clarity',
        target: 'head',
        content: clarity(clarityId),
    };
}

/**
 * Hotjar, kept consent-gated exactly as before: `type="text/plain"` keeps the
 * browser from executing it, and CookieHub activates it once the `analytics`
 * category is granted.
 */
function hotjarScript(hotjarId: number): ScriptDescriptor {
    return {
        id: 'hotjar',
        target: 'head',
        attributes: { type: 'text/plain', 'data-consent': 'analytics' },
        content: hotjar(hotjarId),
    };
}

function jiraCollectorScripts(srcs: string[]): ScriptDescriptor[] {
    return srcs.map((src, index) => ({
        id: srcs.length > 1 ? `jira-collector-${index}` : 'jira-collector',
        src,
        target: 'head',
        attributes: { type: 'text/javascript' },
    }));
}
